import { Selecting, useToastProvider } from '@qualio/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentStatus } from '../../../../../../../../api/model/document';
import { DeleteEntityModal } from '../../../../../../DeleteEntityModal/index';
import { useTableDataInvalidation } from '../../../../hooks/useTableDataInvalidation';
import { WorkspaceTabs } from '../../../../WorkspaceV2';
import {
  hasAllowedStatusesForAction,
  hasRequiredPermissionsForAction,
  isValidTabForAction,
} from '../../../BulkActions';
import { BulkActionButton } from '../../BulkActionButton/index';
import { handleStatusChange } from '../ChangeStatusService';
import {
  ChangeStatusButtonProps,
  IsChangeStatusActionAllowedCallback,
} from '../types';

export const DeleteButton = (props: ChangeStatusButtonProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const onClose = () => setIsModalOpen(false);
  const [isDeletingDocs, setIsDeletingDocs] = useState(false);
  const { selectedDocuments } = props;
  const selection = Selecting.useSelectionMaybe();
  const { invalidate } = useTableDataInvalidation();

  const toastActionStringActive = t('buttons.delete').toLowerCase();
  const toastActionStringPassive = t('statuses.deleted').toLowerCase();

  const handleDelete = async () => {
    setIsDeletingDocs(true);
    await handleStatusChange(
      selectedDocuments,
      { status: DocumentStatus.Deleted },
      selection?.setSelection as any,
      showToast,
      toastActionStringActive,
      toastActionStringPassive,
    );
    setIsDeletingDocs(false);
    setIsModalOpen(false);
    void invalidate(props.currentTab);
  };

  const canDelete: IsChangeStatusActionAllowedCallback = (
    user,
    statuses,
    activeTab,
    requiredPermissions,
  ) => {
    if (
      !hasRequiredPermissionsForAction(requiredPermissions, user.permissions) ||
      !isValidTabForAction(
        [WorkspaceTabs.YOUR_DOCUMENTS, WorkspaceTabs.ALL_DOCUMENTS],
        activeTab,
      )
    ) {
      return false;
    }
    return hasAllowedStatusesForAction([DocumentStatus.Draft], statuses);
  };

  const modalText = (
    <p>
      {t('modals.areYouSureDeleteDocument')} {selectedDocuments.length}{' '}
      {selectedDocuments.length > 1
        ? t('labels.documents')
        : t('labels.document')}
      ?{' '}
      {t('buttons.undoDelete').replace(
        'Undo delete',
        'The action can be undone at the document level',
      )}
      .
    </p>
  );

  const entityName =
    selectedDocuments.length > 1 ? t('labels.documents') : t('labels.document');

  return (
    <>
      <BulkActionButton
        {...props}
        isActionAllowed={canDelete}
        onClick={() => setIsModalOpen(true)}
      />
      <DeleteEntityModal
        isDeletingEntity={isDeletingDocs}
        isOpen={isModalOpen}
        onCloseModal={onClose}
        deleteHandler={handleDelete}
        isDestructive={false}
        modalText={modalText}
        entityName={entityName}
      />
    </>
  );
};
